import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import SwiperCore, {Autoplay, Pagination, Scrollbar, SwiperOptions} from 'swiper';
import {ConnectionService} from '@services/connection-service';
import {DeliveryDateService} from '@services/delivery-date-service/delivery-date.service';
import {TimeService} from '@services/time-service';
import {WeeklyJourneyData, WeeklyJourneyType} from '../../types/weekly-journey.types';
import {EventLoggerService} from '@services/event-logger-service';
import {Router} from '@angular/router';
import {AppConfig} from '../../../app.config';
import {AppWebBridgeService} from '@services/app-web-bridge-service';
import {BroadcastService} from '@services/broadcast-service';
import {WindowRefService} from '@services/window-ref-service';

SwiperCore.use([Autoplay, Scrollbar, Pagination]);

@Component({
  selector: 'app-weekly-journey',
  templateUrl: './weekly-journey.component.html',
  styleUrls: ['../../../user/user-view/user-home/user-home.scss'],
  standalone: false,
})
export class WeeklyJourneyComponent implements OnInit, OnChanges {
  @Input() journeyType: WeeklyJourneyType;
  @Input() journeyData: WeeklyJourneyData;

  // Event emitters for different actions
  @Output() openStaticArticle = new EventEmitter<string>();

  // Component properties
  carouselConfigWeeklyJourney: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 12,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    watchOverflow: true,
    centeredSlides: false,
  };

  expectedDeliveryDate: { from?: Date, to?: Date } = {};
  deliveryIntervalInDays: number = 2;
  kitImg: string = '';
  orderId: string;
  order: any;

  // Enum reference for template
  WeeklyJourneyType = WeeklyJourneyType;

  constructor(
    public conn: ConnectionService,
    public timeService: TimeService,
    public deliveryDateService: DeliveryDateService,
    public readonly appConfig: AppConfig,
    private readonly eventLogger: EventLoggerService,
    private readonly router: Router,
    private readonly appWebBridgeService: AppWebBridgeService,
    private readonly broadcast: BroadcastService,
    private readonly window: WindowRefService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.initializeComponentData();
  }

  ngOnChanges(): void {
    this.updateComponentData();
  }

  private async initializeComponentData(): Promise<void> {
    switch (this.journeyType) {
      case WeeklyJourneyType.WEEK_ZERO:
        await this.initWeekZero();
        break;
      default:
        break;
    }
  }

  private updateComponentData(): void {
    if ((this.journeyType === WeeklyJourneyType.WEEK_ZERO || this.journeyType === WeeklyJourneyType.WEEK_ONE_PART_ONE) && this.journeyData?.latestPlacedRegimenOrder) {
      this.kitImg = this.journeyData.latestPlacedRegimenOrder?.get('regimen')?.get('regimenCoverImage') ?? 'https://storage.googleapis.com/heallo-other-user-files/doctor_api_pratham_gupta_06102023_1686400286033.png';
    }
  }

  // Week Zero specific initialization
  private async initWeekZero(): Promise<void> {
    if (this.journeyData?.latestPlacedRegimenOrder) {
      this.orderId = this.journeyData.latestPlacedRegimenOrder.id;
      this.order = await this.conn.findOrderWithSignedURL(this.orderId);
      await this.setDeliveryDate();
    }
  }

  private async setDeliveryDate(): Promise<void> {
    try {
      const res = await this.conn.getExpectedDeliveryDateByOrderId(this.orderId);
      const responseDateISO = res?.expectedDeliveryDate;
      this.expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(
        responseDateISO,
        this.deliveryIntervalInDays,
      );
    } catch (error) {
      this.expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(null, this.deliveryIntervalInDays);
    }
  }


  async handleOrder(url: string): Promise<void> {
    const isiOSDevice = (await this.appWebBridgeService.requestOSInformation()) === 'iOS' || this.window.isSafariBrowser();
    if (isiOSDevice) this.conn.navigateToURL(url);
    else this.broadcast.broadcast('OPEN_IN_NEW_TAB', { url });
  }

  handleDoctor(): void {
    this.conn.navigateToURL(`/doctor/${this.journeyData?.allocatedDoctor.id}`);
  }

  handleInstructions(): void {
    this.router.navigate(['/user'],
      { queryParams: { class: this.appConfig.Shared.Regimen.Class.FACE,
          username: this.journeyData?.user?.get('username'),
          tab: 'regimen',
          instructions: true } });
  }

  handleDiet(): void {
    this.router.navigate(['/notice']);
  }

  handleHelp(): void {
    this.router.navigate(['/support']);
  }

  handleArticle(articleID: string): void {
    this.router.navigate([`/article/${articleID}`]);
  }

  handleCheckups(card: string): void {
    this.router.navigate(['/user/checkup']);
  }

  handleTakePhoto(): void {
    this.conn.navigateToURL('/user/instantCheckup/preview?tag=FRONT_FACE');
  }

  handleUserStories(): void {
    this.conn.navigateToURL('/user/testimonialsNew');
  }

  handleExplore(exploreId: string): void {
    this.conn.navigateToURL(`/user?tab=explore&id=${exploreId}`);
  }

  handleOrderTracking(orderId: string): void {
    this.conn.navigateToURL(`/user/order/${orderId}`);
  }

  // CTA handlers for different journey types
  handleCTA(): void {
    switch (this.journeyType) {
      case WeeklyJourneyType.WEEK_ONE_PART_ONE:
      case WeeklyJourneyType.WEEK_ONE_PART_TWO:
        this.conn.navigateToURL('https://app.cureskin.com/user?tab=explore&id=iv6pdwK1x9');
        break;
      case WeeklyJourneyType.WEEK_TWO_PART_ONE:
      case WeeklyJourneyType.WEEK_TWO_PART_TWO:
      case WeeklyJourneyType.WEEK_THREE:
        this.conn.navigateToURL('https://app.cureskin.com/user/checkup');
        break;
      case WeeklyJourneyType.WEEK_FOUR:
        this.conn.navigateToURL('https://app.cureskin.com/article/7T2BaIQHxh');
        break;
      default:
        break;
    }
  }

  handleCardCta(ctaAction: string, ctaIdentifier: string, articleId?: string, exploreId?: string): void {
    this.sendClickEvent(ctaIdentifier);
    switch (ctaAction) {
      case 'doctor':
        this.handleDoctor();
        break;
      case 'instructions':
        this.handleInstructions();
        break;
      case 'diet':
        this.handleDiet();
        break;
      case 'article':
        this.handleArticle(articleId);
        break;
      case 'help':
        this.handleHelp();
        break;
      case 'checkup':
        this.handleCheckups('card');
        break;
      case 'takePhoto':
        this.handleTakePhoto();
        break;
      case 'userStories':
        this.handleUserStories();
        break;
      case 'explore':
        this.handleExplore(exploreId);
        break;
      case 'trackOrder':
        this.handleOrderTracking(this.journeyData?.latestPlacedRegimenOrder?.id);
        break;
      default:
        break;
    }
  }

  handleTitleButton(buttonAction: string, ctaIdentifier: string): void {
    this.sendClickEvent(ctaIdentifier);
    switch (buttonAction) {
      case 'trackOrder':
        this.handleOrder(this.journeyData?.latestPlacedRegimenOrder?.get('trackingURL'));
        break;
      case 'watchVideo':
      case 'knowMore':
      case 'takeCheckup':
        this.handleCTA();
        break;
      default:
        break;
    }
  }


  sendClickEvent(ctaIdentifier: string): void {
    const eventData = {
      name: ctaIdentifier,
      version: !!this.journeyData?.weeklyJourneyVariant?.variant2 ? 2 : 1,
    }
    this.eventLogger.cleverTapEvent('click', JSON.stringify(eventData));
  }
}
